/**
 * Recovery for the one failure a deployed SPA cannot design away: a tab that was
 * open when a deploy replaced the hashed chunk it is about to import. The import
 * rejects, and at boot there is no React mounted yet to catch it — the page just
 * stops. Reloading fixes it, because `index.html` is served no-cache and names
 * the new hashes.
 *
 * The whole risk here is looping, so a reload is marked in sessionStorage and
 * happens at most once per tab until a boot succeeds and clears the mark.
 */

const MARK_KEY = "crimp:chunk-reload";

/**
 * Every wording the engines use for "I could not fetch that module". Chrome and
 * Firefox name the module; Safari's `Load failed` is its generic fetch failure,
 * which is only safe to read as a chunk error because callers apply this to
 * `import()` rejections and nothing else.
 */
const CHUNK_ERROR_PATTERNS = [
  /failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /importing a module script failed/i,
  /failed to load module script/i,
  /unable to preload css/i,
  /load failed/i,
];

type ReloadEnv = {
  /** `null` means storage is unreachable, not that it is empty. */
  storage: Pick<Storage, "getItem" | "setItem" | "removeItem"> | null;
  reload: () => void;
};

function sessionStore(): ReloadEnv["storage"] {
  try {
    // Safari in private mode throws on the property access, not just on write.
    return globalThis.sessionStorage ?? null;
  } catch {
    return null;
  }
}

function defaultReload(): void {
  globalThis.location?.reload();
}

function messageOf(error: unknown): string {
  if (typeof error === "string") return error;
  if (error instanceof Error) return error.message;
  if (error && typeof error === "object" && "message" in error) {
    const message = (error as { message?: unknown }).message;
    if (typeof message === "string") return message;
  }
  return "";
}

export function isChunkLoadError(error: unknown): boolean {
  const message = messageOf(error);
  if (!message) return false;
  return CHUNK_ERROR_PATTERNS.some((pattern) => pattern.test(message));
}

export function chunkReloadWasAttempted(
  storage: ReloadEnv["storage"] = sessionStore(),
): boolean {
  try {
    return storage?.getItem(MARK_KEY) === "1";
  } catch {
    return false;
  }
}

export function clearChunkReloadMark(
  storage: ReloadEnv["storage"] = sessionStore(),
): void {
  try {
    storage?.removeItem(MARK_KEY);
  } catch {
    // Nothing to clear is the same outcome as clearing it.
  }
}

/**
 * Reload once if `error` is a chunk that went missing under us. Returns whether
 * the reload was triggered — `false` means the caller still owns the error and
 * should let it surface.
 */
export function recoverFromChunkError(
  error: unknown,
  env: Partial<ReloadEnv> = {},
): boolean {
  if (!isChunkLoadError(error)) return false;

  const storage = "storage" in env ? env.storage : sessionStore();
  // A reload we cannot remember having tried is a reload loop, so no storage
  // means no recovery — the error surfaces instead.
  if (!storage) return false;
  if (chunkReloadWasAttempted(storage)) return false;

  try {
    storage.setItem(MARK_KEY, "1");
  } catch {
    return false;
  }

  (env.reload ?? defaultReload)();
  return true;
}
